import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getCleanPath } from 'js/utils/url.util';

/**
 * Event fires when player navigates to another page.
 * @memberOf Playtech.API.events
 * @event navigate
 * @property pathname {String} Path of a page.
 * @property cleanPathname {String} Path of a page without language code.
 * @property prevPathname {String} Path of a previous page.
 * @property cleanPrevPathname {String} Path of a previous page without language code.
 * @example Playtech.on('navigate', ({ pathname, cleanPathname, prevPathname, cleanPrevPathname }) => {
 *     // /en/sportsbookapp /sportsbookapp /en/home /home
 *     // console.log(pathname, cleanPathname, prevPathname, cleanPrevPathname);
 * });
 */
export const EVENT_ON_NAVIGATE = 'navigate';

export const useOnNavigate = (eventTarget) => {
    const prevPathnameRef = useRef('');
    const { pathname } = useLocation();

    useEffect(() => {
        const event = new CustomEvent(EVENT_ON_NAVIGATE, {
            detail: {
                pathname,
                cleanPathname: getCleanPath(pathname),
                prevPathname: prevPathnameRef.current,
                cleanPrevPathname: getCleanPath(prevPathnameRef.current)
            }
        });

        /**
         * @memberOf Playtech.API.events
         * @fires navigate
         */
        eventTarget.dispatchEvent(event);

        prevPathnameRef.current = pathname;
    }, [pathname, eventTarget]);
};
